import * as React from 'react';
import { DatasheetHolderSelectionBox } from './datasheet-holder.types';

export type DatasheetHolderProps = React.PropsWithChildren<{
  hasSelectedDatasheetElements: boolean;
  style?: React.CSSProperties;
  className?: string;
  reference:  React.RefObject<HTMLDivElement>;
  selectionDisabled: boolean;
  onSelectionReset: () => void;
  onSelectionEnd: (box: DatasheetHolderSelectionBox) => void;
  printableDatasheetHolderId: string;
}>;

export const useDatasheetHolderController = (props: DatasheetHolderProps) => {
  const { reference, onSelectionEnd, hasSelectedDatasheetElements, onSelectionReset } = props;

  const [isSelecting, setIsSelecting] = React.useState(false);
  const [startCoords, setStartCoords] = React.useState<{ x: number; y: number } | null>(null);
  const [selectionBox, setSelectionBox] = React.useState<DatasheetHolderSelectionBox | null>(null);

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isSelecting || !startCoords || !reference.current) return;
   
    const parentRect = reference.current.getBoundingClientRect();
    const currentX = e.clientX - parentRect.left;
    const currentY = e.clientY - parentRect.top;

    const width = Math.abs(currentX - startCoords.x);
    const height = Math.abs(currentY - startCoords.y);
    const x = Math.min(currentX, startCoords.x);
    const y = Math.min(currentY, startCoords.y);

    setSelectionBox({ y, x, width, height });
  };

  const handleMouseUp = () => {
    if (!selectionBox && hasSelectedDatasheetElements) {
      onSelectionReset();
    }

    if (isSelecting && selectionBox && onSelectionEnd) {
      onSelectionEnd(selectionBox);
    }
    setIsSelecting(false);
    setSelectionBox(null);
    setStartCoords(null);
  };

  const handleMouseDown = React.useCallback((e: React.MouseEvent) => {
    if (reference.current) {
      const parentRect = reference.current.getBoundingClientRect();

      setStartCoords({
        x: e.clientX - parentRect.left,
        y: e.clientY - parentRect.top,
      });
      setIsSelecting(true);
    }
  }, []);

  return {
    handleMouseUp,
    handleMouseMove,
    handleMouseDown,
    isSelecting,
    selectionBox,
  };
};